import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { http, setAuthToken, getAuthToken } from '@/api/client'

export interface UserProfile {
  id: number
  username: string
  displayName?: string
  role: string
  roles?: string[]
  tenantId?: string
  avatarInitials?: string
  /** 会话中心工具详情（参数/返回）显示偏好的生效值（非管理员恒为 false） */
  showToolDetails?: boolean
}

interface LoginResponse {
  token: string
  userId: number
  username: string
  roles: string[]
}

/** 后端 /auth/me 返回结构（字段名与 UserProfile 不同，需映射） */
interface MeResponse {
  userId: number
  username: string
  realName?: string
  roles: string[]
  showToolDetails?: boolean
}

function mapMe(me: MeResponse): UserProfile {
  return {
    id: me.userId,
    username: me.username,
    displayName: me.realName || me.username,
    role: me.roles?.[0] ?? '',
    roles: me.roles,
    showToolDetails: me.showToolDetails,
  }
}

export const useAuthStore = defineStore(
  'auth',
  () => {
    const token = ref<string | null>(getAuthToken())
    const user = ref<UserProfile | null>(null)

    const isAuthenticated = computed(() => !!token.value)
    const isAdmin = computed(() => !!user.value?.roles?.includes('ADMIN'))

    async function login(username: string, password: string) {
      const payload = await http.post<LoginResponse>('/auth/login', { username, password })
      token.value = payload.token
      setAuthToken(payload.token)
      user.value = {
        id: payload.userId,
        username: payload.username,
        displayName: payload.username,
        role: payload.roles[0] ?? '',
        roles: payload.roles,
      }
      // 拉取完整资料（realName、工具详情偏好等）；失败时保留登录返回的基础资料
      try {
        await fetchMe()
      } catch {
        // 忽略：基础资料已可用
      }
    }

    async function fetchMe() {
      if (!token.value) return
      user.value = mapMe(await http.get<MeResponse>('/auth/me'))
    }

    /** 更新「会话工具详情」显示偏好（仅管理员有权限，后端会校验）。 */
    async function setShowToolDetails(value: boolean) {
      const me = await http.put<MeResponse>('/auth/me/tool-details', { showToolDetails: value })
      if (user.value) user.value.showToolDetails = me.showToolDetails
    }

    function logout() {
      token.value = null
      user.value = null
      setAuthToken(null)
    }

    return { token, user, isAuthenticated, isAdmin, login, fetchMe, setShowToolDetails, logout }
  },
  {
    persist: {
      key: 'ai-agent-auth',
      storage: localStorage,
      pick: ['user'],
    },
  },
)
